/**
 * The versioned JSON interchange format.
 *
 * Importing is the app's only untrusted-input path, so everything here is
 * written defensively: hand-rolled validation with no casts that skip checks,
 * a size limit applied *before* `JSON.parse`, prototype-polluting keys stripped,
 * every string clamped, and every enum checked against its allowed values.
 * Anything unexpected downgrades to a warning and a safe default rather than
 * throwing — a slightly-wrong file should still open.
 *
 * The AST and the graph are deliberately *not* persisted. They are pure
 * functions of the source, so storing them would only create a way for a file
 * to disagree with itself.
 */
import { BRANDING } from '../config/branding';
import type { FlowDirection } from '../graph/model/types';

export const CURRENT_FORMAT_VERSION = 1;

/** Format ids we still open. Renaming the product must not orphan old files. */
export const ACCEPTED_FORMAT_IDS: readonly string[] = [BRANDING.fileFormatId, 'flowscript'];

export type ThemePreferenceValue = 'system' | 'light' | 'dark';
export type FunctionDisplayValue = 'expanded' | 'collapsed';

export interface DocumentProject {
  readonly id: string;
  readonly name: string;
  readonly source: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface DocumentDiagramSettings {
  readonly direction: FlowDirection;
  readonly theme: ThemePreferenceValue;
  readonly functionDisplay: FunctionDisplayValue;
  readonly expandedFunctions: readonly string[];
  readonly yesLabel: string;
  readonly noLabel: string;
}

export interface FlowScriptDocument {
  readonly format: string;
  readonly version: number;
  readonly project: DocumentProject;
  readonly diagramSettings: DocumentDiagramSettings;
}

export type ImportErrorCode =
  | 'not-json'
  | 'not-an-object'
  | 'unknown-format'
  | 'unsupported-version'
  | 'missing-field'
  | 'invalid-field'
  | 'too-large';

export interface ImportError {
  readonly code: ImportErrorCode;
  readonly message: string;
  readonly detail?: string;
}

export type ImportResult =
  | {
      readonly ok: true;
      readonly document: FlowScriptDocument;
      readonly migratedFrom?: number;
      readonly warnings: readonly string[];
    }
  | { readonly ok: false; readonly error: ImportError };

export interface ImportLimits {
  readonly maxBytes: number;
  readonly maxNameLength: number;
  readonly maxSourceLength: number;
  readonly maxExpandedFunctions: number;
}

export const DEFAULT_IMPORT_LIMITS: ImportLimits = {
  maxBytes: 5_000_000,
  maxNameLength: 200,
  maxSourceLength: 2_000_000,
  maxExpandedFunctions: 500,
};

export const DEFAULT_DIAGRAM_SETTINGS: DocumentDiagramSettings = {
  direction: 'top-to-bottom',
  theme: 'system',
  functionDisplay: 'expanded',
  expandedFunctions: [],
  yesLabel: 'Yes',
  noLabel: 'No',
};

/* ------------------------------------------------------------------ *
 * Serialising
 * ------------------------------------------------------------------ */

export interface SerializeInput {
  readonly project: DocumentProject;
  readonly diagramSettings?: Partial<DocumentDiagramSettings>;
}

export function buildDocument(input: SerializeInput): FlowScriptDocument {
  return {
    format: BRANDING.fileFormatId,
    version: CURRENT_FORMAT_VERSION,
    project: { ...input.project },
    diagramSettings: { ...DEFAULT_DIAGRAM_SETTINGS, ...input.diagramSettings },
  };
}

export function serializeDocument(input: SerializeInput): string {
  return `${JSON.stringify(buildDocument(input), null, 2)}\n`;
}

/* ------------------------------------------------------------------ *
 * Migrations
 * ------------------------------------------------------------------ */

type UnknownRecord = Record<string, unknown>;

/**
 * Version N -> N+1 transforms, keyed by the version being upgraded *from*.
 *
 * Empty today because there has only ever been one version. When the format
 * changes, bump `CURRENT_FORMAT_VERSION` and add the entry that turns the old
 * shape into the new one; `parseDocument` will chain them automatically.
 */
const MIGRATIONS: Readonly<Record<number, (doc: UnknownRecord) => UnknownRecord>> = {};

/* ------------------------------------------------------------------ *
 * Parsing
 * ------------------------------------------------------------------ */

const FORBIDDEN_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Rebuilds a parsed JSON value with prototype-polluting keys removed.
 *
 * `JSON.parse` itself is safe, but the moment a payload's `__proto__` key is
 * spread or assigned onto another object it stops being safe. Stripping here
 * means no later code has to remember.
 */
function sanitize(value: unknown, depth = 0): unknown {
  if (depth > 32) return null; // absurdly nested input is not worth walking
  if (Array.isArray(value)) return value.map((item) => sanitize(item, depth + 1));
  if (value === null || typeof value !== 'object') return value;

  const out: UnknownRecord = Object.create(null) as UnknownRecord;
  for (const [key, item] of Object.entries(value as UnknownRecord)) {
    if (FORBIDDEN_KEYS.has(key)) continue;
    out[key] = sanitize(item, depth + 1);
  }
  return out;
}

function fail(code: ImportErrorCode, message: string, detail?: string): ImportResult {
  return { ok: false, error: detail === undefined ? { code, message } : { code, message, detail } };
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

function validIsoDate(value: unknown): value is string {
  return typeof value === 'string' && ISO_DATE.test(value) && !Number.isNaN(Date.parse(value));
}

export interface ParseOptions {
  readonly limits?: Partial<ImportLimits>;
  /** Injected for determinism in tests. */
  readonly now?: () => string;
}

export function parseDocument(text: string, options: ParseOptions = {}): ImportResult {
  const limits = { ...DEFAULT_IMPORT_LIMITS, ...options.limits };
  const now = options.now ?? (() => new Date().toISOString());
  const warnings: string[] = [];

  // Size is checked on the raw text: parsing a 500 MB string to find out it is
  // too big defeats the purpose of the limit.
  if (text.length > limits.maxBytes) {
    return fail(
      'too-large',
      `The file is too large to import (${text.length} characters; the limit is ${limits.maxBytes}).`,
    );
  }

  let raw: unknown;
  try {
    raw = JSON.parse(text) as unknown;
  } catch (error) {
    return fail(
      'not-json',
      'That file is not valid JSON.',
      error instanceof Error ? error.message : undefined,
    );
  }

  const clean = sanitize(raw);
  if (!isRecord(clean)) {
    return fail('not-an-object', 'A FlowScript file must contain a JSON object.');
  }

  if (typeof clean.format !== 'string' || !ACCEPTED_FORMAT_IDS.includes(clean.format)) {
    return fail(
      'unknown-format',
      'This does not look like a FlowScript file.',
      typeof clean.format === 'string' ? `Found format "${clean.format}".` : undefined,
    );
  }

  const version = clean.version;
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    return fail('invalid-field', 'The file does not declare a valid version number.');
  }
  if (version > CURRENT_FORMAT_VERSION) {
    return fail(
      'unsupported-version',
      `This file was written by a newer version of ${BRANDING.name} (format ${version}).`,
      `This build understands format ${CURRENT_FORMAT_VERSION}.`,
    );
  }

  let working = clean;
  const migratedFrom = version < CURRENT_FORMAT_VERSION ? version : undefined;
  for (let v = version; v < CURRENT_FORMAT_VERSION; v += 1) {
    const migrate = MIGRATIONS[v];
    if (!migrate) {
      return fail('unsupported-version', `No migration exists from format ${v}.`);
    }
    working = migrate(working);
  }

  if (!isRecord(working.project)) {
    return fail('missing-field', 'The file has no `project` section.');
  }
  const project = working.project;

  if (typeof project.source !== 'string') {
    return fail('missing-field', 'The project has no `source` text.');
  }
  if (project.source.length > limits.maxSourceLength) {
    return fail('too-large', 'The project source is longer than this build can open.');
  }

  const name = typeof project.name === 'string' ? project.name.slice(0, limits.maxNameLength) : '';
  if (typeof project.name !== 'string') {
    warnings.push('The project had no name; a default was used.');
  }

  const id =
    typeof project.id === 'string' && project.id.length > 0 && project.id.length <= 128
      ? project.id
      : undefined;
  if (id === undefined)
    warnings.push('The project id was missing or invalid; a new one was assigned.');

  const createdAt = validIsoDate(project.createdAt) ? project.createdAt : now();
  if (!validIsoDate(project.createdAt)) warnings.push('The creation date was invalid.');
  const updatedAt = validIsoDate(project.updatedAt) ? project.updatedAt : now();
  if (!validIsoDate(project.updatedAt)) warnings.push('The last-modified date was invalid.');

  const settings = readSettings(working.diagramSettings, limits, warnings);

  return {
    ok: true,
    ...(migratedFrom !== undefined ? { migratedFrom } : {}),
    warnings,
    document: {
      format: BRANDING.fileFormatId,
      version: CURRENT_FORMAT_VERSION,
      project: {
        id: id ?? '',
        name: name.length > 0 ? name : 'Imported flow',
        source: project.source,
        createdAt,
        updatedAt,
      },
      diagramSettings: settings,
    },
  };
}

function readSettings(
  raw: unknown,
  limits: ImportLimits,
  warnings: string[],
): DocumentDiagramSettings {
  if (!isRecord(raw)) return DEFAULT_DIAGRAM_SETTINGS;

  const oneOf = <T extends string>(
    value: unknown,
    allowed: readonly T[],
    fallback: T,
    field: string,
  ): T => {
    if (typeof value === 'string' && (allowed as readonly string[]).includes(value)) {
      return value as T;
    }
    if (value !== undefined) warnings.push(`Unrecognised \`${field}\`; the default was used.`);
    return fallback;
  };

  const label = (value: unknown, fallback: string, field: string): string => {
    if (typeof value === 'string' && value.trim().length > 0) return value.slice(0, 40);
    if (value !== undefined) warnings.push(`Unrecognised \`${field}\`; the default was used.`);
    return fallback;
  };

  let expandedFunctions: string[] = [];
  if (Array.isArray(raw.expandedFunctions)) {
    expandedFunctions = raw.expandedFunctions
      .filter((item): item is string => typeof item === 'string')
      .slice(0, limits.maxExpandedFunctions)
      .map((item) => item.slice(0, 128));
    if (expandedFunctions.length !== raw.expandedFunctions.length) {
      warnings.push('Some entries in `expandedFunctions` were dropped.');
    }
  } else if (raw.expandedFunctions !== undefined) {
    warnings.push('`expandedFunctions` was not a list; it was ignored.');
  }

  return {
    direction: oneOf<FlowDirection>(
      raw.direction,
      ['top-to-bottom', 'left-to-right'],
      DEFAULT_DIAGRAM_SETTINGS.direction,
      'direction',
    ),
    theme: oneOf<ThemePreferenceValue>(
      raw.theme,
      ['system', 'light', 'dark'],
      DEFAULT_DIAGRAM_SETTINGS.theme,
      'theme',
    ),
    functionDisplay: oneOf<FunctionDisplayValue>(
      raw.functionDisplay,
      ['expanded', 'collapsed'],
      DEFAULT_DIAGRAM_SETTINGS.functionDisplay,
      'functionDisplay',
    ),
    expandedFunctions,
    yesLabel: label(raw.yesLabel, DEFAULT_DIAGRAM_SETTINGS.yesLabel, 'yesLabel'),
    noLabel: label(raw.noLabel, DEFAULT_DIAGRAM_SETTINGS.noLabel, 'noLabel'),
  };
}
